import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  BookOpen, CheckCircle2, AlertTriangle, AlertOctagon,
  Search, Calculator, Database, Code2, AlertCircle, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  verified: {
    label: 'Verified',
    icon: CheckCircle2,
    badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    dot: 'bg-emerald-500',
    desc: 'Formula correct, computed live from raw data.'
  },
  approximated: {
    label: 'Approximated',
    icon: AlertTriangle,
    badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    dot: 'bg-amber-500',
    desc: 'From precomputed snapshot or partial assumptions. May not respect dynamic filters.'
  },
  fake: {
    label: 'Fake / Static',
    icon: AlertOctagon,
    badge: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
    dot: 'bg-rose-500',
    desc: 'Hardcoded percentages — NOT computed from real data. Needs refactor.'
  },
};

export default function MetricFormulas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchCatalog(); }, []);

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/formulas/catalog`, { credentials: 'include' });
      const j = await res.json();
      if (j.success) setData(j);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) return <div className="p-6 text-muted-foreground" data-testid="formulas-loading">Loading formula catalog…</div>;
  if (!data) return <div className="p-6 text-muted-foreground" data-testid="formulas-error">Failed to load catalog.</div>;

  const { summary, catalog } = data;

  // Filter sections + metrics
  const q = search.toLowerCase().trim();
  const filteredCatalog = catalog
    .map(section => ({
      ...section,
      metrics: section.metrics.filter(m => {
        if (statusFilter !== 'all' && m.status !== statusFilter) return false;
        if (!q) return true;
        return [m.name, m.definition, m.formula, m.source, m.issue].some(t => (t || '').toLowerCase().includes(q));
      })
    }))
    .filter(section => section.metrics.length > 0);

  return (
    <div className="p-6 space-y-6 max-w-7xl" data-testid="metric-formulas-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-primary" />
          Metric Formulas & Definitions
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl">
          Source of truth for every dashboard metric. Each metric shows its <span className="text-foreground">definition</span>,
          the <span className="text-foreground">formula</span>, the <span className="text-foreground">data source</span>, and a
          <span className="text-foreground"> validation status</span>. Use this to audit numbers before production migration.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SumCard label="Total Metrics" value={summary.total_metrics} icon={Calculator} accent="text-blue-400" />
        <SumCard label="Verified" value={summary.verified} icon={CheckCircle2} accent="text-emerald-400" onClick={() => setStatusFilter('verified')} active={statusFilter === 'verified'} />
        <SumCard label="Approximated" value={summary.approximated} icon={AlertTriangle} accent="text-amber-400" onClick={() => setStatusFilter('approximated')} active={statusFilter === 'approximated'} />
        <SumCard label="Fake / Static" value={summary.fake} icon={AlertOctagon} accent="text-rose-400" onClick={() => setStatusFilter('fake')} active={statusFilter === 'fake'} />
      </div>

      {/* Search + filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search metric name, formula, source…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="formula-search-input"
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <FilterPill active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} testId="filter-all">All</FilterPill>
          <FilterPill active={statusFilter === 'verified'} onClick={() => setStatusFilter('verified')} testId="filter-verified" color="emerald">Verified</FilterPill>
          <FilterPill active={statusFilter === 'approximated'} onClick={() => setStatusFilter('approximated')} testId="filter-approximated" color="amber">Approximated</FilterPill>
          <FilterPill active={statusFilter === 'fake'} onClick={() => setStatusFilter('fake')} testId="filter-fake" color="rose">Fake</FilterPill>
        </div>
      </div>

      {/* Critical Alert */}
      {summary.fake > 0 && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3" data-testid="formulas-fake-warning">
          <AlertOctagon className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-rose-300">Critical: {summary.fake} metrics use FAKE data</div>
            <div className="text-rose-200/80 mt-0.5">
              These dashboards display hardcoded percentages instead of real customer behavior. Production migration is BLOCKED until these are refactored to compute live from raw transactions.
            </div>
          </div>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-6">
        {filteredCatalog.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="formulas-no-results">
            No metrics match your search.
          </div>
        )}

        {filteredCatalog.map((section, sidx) => (
          <div key={sidx} data-testid={`formula-section-${sidx}`}>
            <div className="flex items-baseline gap-2 mb-3">
              <h2 className="text-xl font-bold">{section.dashboard}</h2>
              <span className="text-sm text-muted-foreground">›</span>
              <span className="text-sm text-muted-foreground">{section.category}</span>
              <Badge variant="outline" className="ml-auto text-xs">{section.metrics.length} metrics</Badge>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {section.metrics.map((m, midx) => {
                const meta = STATUS_META[m.status] || STATUS_META.approximated;
                const Icon = meta.icon;
                return (
                  <Card key={midx} className="border-border bg-card/50 backdrop-blur hover:border-primary/40 transition" data-testid={`formula-metric-${sidx}-${midx}`}>
                    <CardContent className="p-4 space-y-3">
                      {/* Header */}
                      <div className="flex items-start gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full mt-2 flex-shrink-0 ${meta.dot}`}></span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm leading-tight">{m.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">{m.definition}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${meta.badge}`}>
                          <Icon className="w-3 h-3" />{meta.label}
                        </span>
                      </div>

                      {/* Formula */}
                      <div className="rounded-md bg-muted/40 border border-border/50 p-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1 mb-1">
                          <Code2 className="w-3 h-3" />Formula
                        </div>
                        <code className="text-xs font-mono text-foreground/90 break-words whitespace-pre-wrap">{m.formula}</code>
                      </div>

                      {/* Source */}
                      <div className="text-xs">
                        <span className="text-muted-foreground inline-flex items-center gap-1">
                          <Database className="w-3 h-3" />Source:
                        </span>{' '}
                        <code className="text-xs font-mono text-foreground/80 break-words">{m.source}</code>
                      </div>

                      {/* Issue (if any) */}
                      {m.issue && (
                        <div className="text-xs flex items-start gap-1.5 p-2 rounded bg-amber-500/5 border border-amber-500/20">
                          <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                          <div className="text-amber-200/90">{m.issue}</div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend Footer */}
      <div className="border-t border-border pt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-muted-foreground">
        {Object.entries(STATUS_META).map(([k, v]) => {
          const Icon = v.icon;
          return (
            <div key={k} className="flex items-start gap-2">
              <Icon className={`w-4 h-4 ${v.badge.split(' ')[1]}`} />
              <div>
                <div className="font-semibold text-foreground">{v.label}</div>
                <div>{v.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SumCard = ({ label, value, icon: Icon, accent, onClick, active }) => (
  <Card
    className={`border-border bg-card/50 backdrop-blur transition cursor-pointer ${active ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/30'}`}
    onClick={onClick}
    data-testid={`summary-card-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
  >
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold mt-1">{value}</div>
      </div>
      <Icon className={`w-7 h-7 ${accent} opacity-70`} />
    </CardContent>
  </Card>
);

const FilterPill = ({ active, onClick, children, testId, color = 'primary' }) => {
  const colorMap = {
    primary: 'border-primary text-primary',
    emerald: 'border-emerald-500/40 text-emerald-400',
    amber: 'border-amber-500/40 text-amber-400',
    rose: 'border-rose-500/40 text-rose-400',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-2.5 py-1 rounded-full border transition ${active ? colorMap[color] + ' bg-muted/40 font-semibold' : 'border-border text-muted-foreground hover:bg-muted/40'}`}
    >
      {children}
    </button>
  );
};
